import React from 'react';
import { FiChevronDown } from 'react-icons/fi';
import * as translator from '../../../../../utils/translator';
import { clientConfig } from '../../../../../config';
import { UpsellItem } from 'data-types';
import BadgeNew from '../../BadgeNew/BadgeNew';
import { MainTracker } from '../../../../../PixelTrackers/MainTracker';
import './UpsellProductCard.css';



interface UpsellProductCardProps {
	item: UpsellItem;
	language: string;
}

// Discounted prices arrive as floats (price * 0.9 → 62.910000000000004); never show more than
// two decimals, and drop trailing zeros so "35.1" / "39" keep their current look.
const formatPrice = (value: number): string => String(Number(value.toFixed(2)));

const UpsellProductCard: React.FC<UpsellProductCardProps> = ({ item, language }) => {
	const productUrl = `${clientConfig.MAIN_WEBSITE_URL}${item.url}`;

	return (
	<div
		id={`care-profile-upsell-item-${item.productItemID}`}
		className="keen-slider__slide care-profile-upsell-card relative flex flex-col bg-white border border-solid border-[#e3e8e0] rounded-[11px] flex-shrink-0 cursor-pointer hover:shadow-md transition-shadow duration-200"
	>
		<div className="absolute top-[5px] left-0 bg-[#e9fbeb] rounded-r-[4px] px-2 py-0.5">
			<p className="text-[8px]	 font-medium text-[#02a414]">{translator.translate(language, 'my_account', 'CareProfile/Upsell/InStock')}</p>
		</div>

		{item.discountPercentage !== null && (
			<div className="absolute top-2 right-2">
				<BadgeNew show text={`-${item.discountPercentage}%`} color="#f59603" />
			</div>
		)}

		<a href={productUrl} id={`care-profile-upsell-item-${item.productItemID}-details`} className="flex flex-col no-underline" onClick={() => MainTracker.track('custom', 'Upsell Product Clicked', { product: item.title, price: item.price })}>
			<div className="w-full h-[90px] mt-3 overflow-hidden">
				<img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover scale-125" />
			</div>

			<div className="flex items-center justify-center flex-wrap gap-x-1 gap-y-1 px-1.5 mt-1 mb-2">
				{item.biomarkerCount > 0 && (
					<span className="bg-[#f7f7f7] text-[8px] font-medium text-[#269c6e] rounded-[3px] px-1.5 py-0.5 whitespace-nowrap">
						{translator.translate(language, 'my_account', 'CareProfile/Upsell/Tag1').replace('{{count}}', String(item.biomarkerCount))}
					</span>
				)}
				{item.symptomCount > 0 && (
					<span className="bg-[#edfff4] text-[8px] font-medium text-[#269c6e] rounded-[3px] px-1.5 py-0.5 whitespace-nowrap">
						{translator.translate(language, 'my_account', 'CareProfile/Upsell/Tag2').replace('{{count}}', String(item.symptomCount))}
					</span>
				)}
			</div>

			<div className="flex flex-col gap-y-0.5 px-2.5">
				<p className="text-[13px] font-semibold text-care-plus-heading break-words">{item.title}</p>
				<p className="text-xs text-[#656d79]">{translator.translate(language, 'my_account', 'CareProfile/Upsell/Brand')}</p>
				<div className="flex items-center gap-x-2 mt-0.5">
					<span className="text-sm font-semibold text-care-plus-heading">
						{formatPrice(item.price)} {item.currency}
					</span>
					{item.priceBeforeDiscount !== null && (
						<span className="text-xs text-[#99a3b2] line-through">
							{formatPrice(item.priceBeforeDiscount)} {item.currency}
						</span>
					)}
				</div>
			</div>
		</a>

		<a
			href={productUrl}
			id={`care-profile-upsell-item-${item.productItemID}-add-to-cart`}
			className="care-profile-add-to-cart flex items-center min-h-8 py-1.5 rounded-[4px] mx-2.5 mt-auto mb-2.5 no-underline"
			onClick={() => MainTracker.track('custom', 'Upsell Added To Cart', { product: item.title, price: item.price, discount_applied: item.discountPercentage !== null })}
		>
			<span className="flex items-center gap-x-[3px] pl-1.5 pr-2 flex-shrink-0">
				<span className="flex items-center justify-center w-3.5 h-3.5 rounded-full bg-white flex-shrink-0">
					<span className="care-profile-add-to-cart-qty text-[8px] font-medium leading-none">1</span>
				</span>
				<FiChevronDown size={10} className="text-white flex-shrink-0" />
			</span>
			<span className="w-px self-stretch bg-white/50 flex-shrink-0" />
			<span className="flex-1 text-center text-[10px] leading-[1.15] font-semibold text-white px-1">
				{translator.translate(language, 'my_account', 'CareProfile/Upsell/AddToCart')}
			</span>
		</a>
	</div>
	);
};

export default UpsellProductCard;
